import { useState, useEffect, useRef, useMemo } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, X, Sparkles } from "lucide-react";
import { TradingSymbol } from "@/utils/mockData";
import { cn } from "@/lib/utils";
import { apiClient } from "@/utils/apiClient";
import { usePriceContext } from "@/context/PriceContext";
import ReactMarkdown from "react-markdown";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface SymbolAIChatDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  symbol: TradingSymbol;
}

export function SymbolAIChatDrawer({ open, onOpenChange, symbol }: SymbolAIChatDrawerProps) {
  const { symbols } = usePriceContext();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFirstInteraction, setIsFirstInteraction] = useState(true);
  const [bidColor, setBidColor] = useState<"increase" | "decrease" | "neutral">("neutral");
  const [askColor, setAskColor] = useState<"increase" | "decrease" | "neutral">("neutral");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const prevBidRef = useRef<number>(symbol.sellPrice);
  const prevAskRef = useRef<number>(symbol.buyPrice);

  // Get live prices for the current symbol
  const liveSymbol = useMemo(() => {
    return symbols.find(s => s.name === symbol.name) || symbol;
  }, [symbols, symbol.name]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Track price changes for dynamic coloring
  useEffect(() => {
    if (liveSymbol.sellPrice !== prevBidRef.current) {
      if (liveSymbol.sellPrice > prevBidRef.current) {
        setBidColor("increase");
      } else if (liveSymbol.sellPrice < prevBidRef.current) {
        setBidColor("decrease");
      }
      prevBidRef.current = liveSymbol.sellPrice;
      
      // Reset color after 1 second
      const timer = setTimeout(() => setBidColor("neutral"), 1000);
      return () => clearTimeout(timer);
    }
  }, [liveSymbol.sellPrice]);

  useEffect(() => {
    if (liveSymbol.buyPrice !== prevAskRef.current) {
      if (liveSymbol.buyPrice > prevAskRef.current) {
        setAskColor("increase");
      } else if (liveSymbol.buyPrice < prevAskRef.current) {
        setAskColor("decrease");
      }
      prevAskRef.current = liveSymbol.buyPrice;
      
      // Reset color after 1 second
      const timer = setTimeout(() => setAskColor("neutral"), 1000);
      return () => clearTimeout(timer);
    }
  }, [liveSymbol.buyPrice]);

  // Reset messages and set fixed suggestions when drawer opens
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInput("");
      setSuggestedQuestions([]);
      setIsInitialLoad(true);
      setIsFirstInteraction(true);
    } else if (isInitialLoad) {
      // Set fixed suggestion questions with dynamic symbol name
      const fixedQuestions = [
        `What's the current trend for ${symbol.name}?`,
        `How volatile is ${symbol.name} today?`,
        `Should I buy or sell ${symbol.name}?`,
        `What are the key levels for ${symbol.name}?`,
        `What's the outlook for ${symbol.name}?`,
      ];
      setSuggestedQuestions(fixedQuestions);
      setIsInitialLoad(false);
    }
  }, [open, symbol.name, isInitialLoad]);

  const formatPrice = (price: number) => {
    return price?.toFixed(liveSymbol.digit || 2) || "0.00";
  };

  const getDiffColor = () => {
    return liveSymbol.change >= 0 ? "text-success" : "text-destructive";
  };

  const symbolContext = {
    symbol: liveSymbol.name,
    bid: formatPrice(liveSymbol.sellPrice),
    ask: formatPrice(liveSymbol.buyPrice),
    ltp: formatPrice(liveSymbol.close),
    high: formatPrice(liveSymbol.high),
    low: formatPrice(liveSymbol.low),
    diffAbs: formatPrice(Math.abs(liveSymbol.change)),
    diffPercent: ((liveSymbol.change / liveSymbol.previousBuyPrice) * 100).toFixed(2),
    exchange: liveSymbol.symbol || "",
    instrumentType: liveSymbol.intrumentType,
  };

  const sendMessage = async (messageText: string, hideUserMessage = false, isInitialCall = false) => {
    if (!messageText.trim()) return;

    const userMessage: Message = { role: "user", content: messageText, timestamp: new Date() };
    if (!hideUserMessage) {
      setMessages((prev) => [...prev, userMessage]);
    }
    setInput("");
    setIsLoading(true);

    try {
      const response = await apiClient.post<{ answer: string; top5?: string[] }>(
        "chat/ask",
        {
          symbol: symbol.name,
          text: messageText,
        }
      );

      // For initial call: only show suggestions, don't show answer
      if (isInitialCall) {
        if (response.top5 && Array.isArray(response.top5) && response.top5.length > 0) {
          setSuggestedQuestions(response.top5);
        }
      } else {
        // For all subsequent calls: show answer and hide suggestions permanently
        const assistantMessage: Message = {
          role: "assistant",
          content: response.answer || "No response received.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
        
        // After first user interaction, permanently hide suggestions
        if (isFirstInteraction) {
          setIsFirstInteraction(false);
        }
        setSuggestedQuestions([]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          timestamp: new Date(),
        },
      ]);
      // Hide suggestions on error too
      setSuggestedQuestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handleQuickQuestion = (question: string) => {
    sendMessage(question);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[95vh] flex flex-col bg-background">
        {/* Header */}
        <DrawerHeader className="border-b px-4 py-4 flex-shrink-0 bg-card/50 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <DrawerTitle className="text-base font-bold">{symbol.name}</DrawerTitle>
                <div className="text-xs text-muted-foreground">AI Assistant</div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Live Price Strip - 3 Column Layout */}
          <div className="grid grid-cols-3 gap-2 text-xs bg-muted/50 rounded-lg px-3 py-2">
            <div className="flex flex-col items-center justify-center">
              <span className="text-muted-foreground text-[10px] mb-0.5">Bid</span>
              <span className={cn(
                "font-semibold transition-colors duration-300",
                bidColor === "increase" && "text-[#00C853]",
                bidColor === "decrease" && "text-[#FF5252]",
                bidColor === "neutral" && "text-muted-foreground"
              )}>
                {symbolContext.bid}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className="text-muted-foreground text-[10px] mb-0.5">Ask</span>
              <span className={cn(
                "font-semibold transition-colors duration-300",
                askColor === "increase" && "text-[#00C853]",
                askColor === "decrease" && "text-[#FF5252]",
                askColor === "neutral" && "text-muted-foreground"
              )}>
                {symbolContext.ask}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <span className="text-muted-foreground text-[10px] mb-0.5">Change</span>
              <span className={cn("font-semibold", getDiffColor())}>
                {symbolContext.diffPercent}%
              </span>
            </div>
          </div>
        </DrawerHeader>

        {/* Chat Messages */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto px-4 py-6 space-y-4 bg-background"
        >
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="p-4 rounded-full bg-primary/10 mb-4">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Ask me anything about {symbol.name}</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Get insights, trends, and analysis powered by AI
              </p>
              
              {/* Quick Questions - Centered */}
              {suggestedQuestions.length > 0 && (
                <div className="w-full max-w-sm space-y-2 animate-fade-in mt-4">
                  <div className="text-xs text-muted-foreground mb-3 font-medium">Quick questions:</div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {suggestedQuestions.map((question, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickQuestion(question)}
                        className="text-xs h-auto py-2 px-3 whitespace-normal rounded-lg bg-background hover:bg-accent"
                        disabled={isLoading}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className="flex flex-col w-full"
            >
              <div
                className={cn(
                  "rounded-2xl px-4 py-3 w-full shadow-sm",
                  message.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-muted/80 rounded-bl-sm"
                )}
              >
                {message.role === "assistant" ? (
                  <div className="text-sm prose prose-sm max-w-none dark:prose-invert [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</div>
                )}
              </div>
              <div className={cn(
                "text-xs text-muted-foreground mt-1 px-2",
                message.role === "user" ? "text-right" : "text-left"
              )}>
                {formatTimestamp(message.timestamp)}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex items-start">
              <div className="rounded-2xl rounded-bl-sm px-4 py-3 bg-muted/80 shadow-sm">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/60 animate-bounce" />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:0.2s]" />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:0.4s]" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t px-4 py-4 flex-shrink-0 bg-card/50 backdrop-blur-sm">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
            className="flex gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask something about ${symbol.name}...`}
              disabled={isLoading}
              className="flex-1 rounded-full border-muted-foreground/20 bg-background focus-visible:ring-primary h-11"
            />
            <Button
              type="submit"
              size="icon"
              disabled={isLoading || !input.trim()}
              className="h-11 w-11 rounded-full shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
